import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import Tracker, {
  ComplexTrackerProps,
} from 'shared/components/vxl/Tracker/Tracker';
import ExpressiveXSText from 'shared/components/vxl/typography/expressive/ExpressiveXSText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSBoldText from 'shared/components/vxl/typography/primary/body/BodyXSBoldText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { isWeb } from 'shared/constants/platform';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';
import { Maybe } from 'shared/utils/types';

export type CreditScoreTileStep = 0 | 1 | 2 | 3 | 4 | 5;
export type CreditScoreRange = {
  name: string;
  description: string;
  min: number;
  max: number;
};
export interface CreditScoreCardProps extends PressableAnalyticsProps {
  // The representation of the consumers credit score.
  creditScore: string;
  // the change in credit score since last time logged int.  can be negative or positive and will swap from green to red text if negative
  creditScoreDelta?: Maybe<number>;
  // The small tagline under the credit score delta.  commonly used to display the last time it was updated
  creditScoreDeltaTagline: string;
  // the step of the credit tracker bar we wish to fill from 0-5, 0 being empty, 5 being full
  fillToStep: CreditScoreTileStep;
  // Tile was tapped
  handleCardPress?: () => void;
  // toggle to disable the card
  isDisabled: boolean;
  // Credit Score Range
  ranges: CreditScoreRange[];
  // Credit Score Grade
  grade: string;
}

export interface CreditScoreTextStackProps {
  creditScore: string;
  creditScoreDelta?: Maybe<number>;
  creditScoreDeltaTagline: string;
  isDisabled: boolean;
}

const CreditScoreTextStack = ({
  creditScore,
  creditScoreDelta,
  creditScoreDeltaTagline,
  isDisabled,
}: CreditScoreTextStackProps) => {
  const { t: translatedText } = useTranslation('componentsStrings', {
    keyPrefix: 'creditScoreCardStrings',
  });
  const isDeltaPositive = creditScoreDelta && creditScoreDelta > 0;
  const noChange = creditScoreDelta === 0;
  const isCreditDeltaShown = isNotNil(creditScoreDelta);
  // Max lines will need to dynamically swap depending on if the above text field (delta display) is present
  const tagLineMaxNumberOfLines = isCreditDeltaShown ? 1 : 3;
  let delta = isNotNil(creditScoreDelta)
    ? `${isDeltaPositive ? '+' : '-'} ${Math.abs(creditScoreDelta)} ${translatedText('delta.pts')}`
    : '';
  let creditDeltaTextStyle = baseStyle.creditScoreDeltaNoChange;

  // if no change then display 'No Change' text and keep black text color
  if (noChange) {
    delta = translatedText('delta.noChange');
  } else {
    creditDeltaTextStyle = isDeltaPositive
      ? baseStyle.creditScoreDeltaPositive
      : baseStyle.creditScoreDeltaNegative;
  }
  return (
    <View style={baseStyle.creditScoreTextStack}>
      <ExpressiveXSText
        numberOfLines={1}
        style={[
          baseStyle.creditScoreText,
          isDisabled && interactiveStyle.disabledText,
        ]}
      >
        {creditScore}
      </ExpressiveXSText>
      <View style={baseStyle.deltaStackContainer}>
        {isCreditDeltaShown ? (
          <TitleSmText
            numberOfLines={1}
            style={[
              creditDeltaTextStyle,
              isDisabled ? interactiveStyle.disabledText : null,
            ]}
          >
            {delta}
          </TitleSmText>
        ) : null}
        <BodyXSText
          numberOfLines={tagLineMaxNumberOfLines}
          style={[
            baseStyle.creditScoreDeltaTagline,
            isDisabled ? interactiveStyle.disabledText : null,
          ]}
        >
          {creditScoreDeltaTagline}
        </BodyXSText>
      </View>
    </View>
  );
};

export const CreditScoreCard = ({
  creditScore,
  creditScoreDelta,
  creditScoreDeltaTagline,
  fillToStep,
  handleCardPress,
  isDisabled,
  ranges,
  grade,
  analyticsId = undefined,
  customAttributes = null,
}: CreditScoreCardProps) => {
  const { t: translatedText } = useTranslation('componentsStrings', {
    keyPrefix: 'creditScoreCardStrings',
  });
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const pressableAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });

  const trackerProps: ComplexTrackerProps = {
    trackerType: 'COMPLEX',
    steps: [
      { length: 4 },
      { length: 2 },
      { length: 2 },
      { length: 2 },
      { length: 2 },
    ],
    fillToStep: fillToStep,
    fillColor: isDisabled
      ? theme.color.text.muted
      : theme.color.expressive.mint,
    hasAnimation: false,
  };
  const labels: string[] = ranges?.map(
    (range: CreditScoreRange) => range?.description,
  );
  const isGradeLabel = (label: string): boolean => grade === label;
  return (
    <PressableWithDelay
      testID="creditScoreCard"
      style={[
        baseStyle.container,
        isFocused ? interactiveStyle.focus : null,
        isHovered ? interactiveStyle.hover : null,
        isPressed ? interactiveStyle.press : null,
      ]}
      onPress={() => {
        pressableAnalytics();
        handleCardPress?.();
        if (isWeb) {
          setIsFocused(false);
        }
      }}
      onBlur={() => handleCardPress && setIsFocused(false)}
      onHoverIn={() => handleCardPress && setIsHovered(true)}
      onHoverOut={() => handleCardPress && setIsHovered(false)}
      onFocus={() => handleCardPress && setIsFocused(true)}
      onPressIn={() => handleCardPress && setIsPressed(true)}
      onPressOut={() => handleCardPress && setIsPressed(false)}
      disabled={isDisabled}
      role="button"
    >
      <BodySmText
        style={isDisabled ? interactiveStyle.disabledText : null}
        numberOfLines={1}
      >
        {translatedText('title')}
      </BodySmText>
      <CreditScoreTextStack
        creditScore={creditScore}
        creditScoreDelta={creditScoreDelta}
        creditScoreDeltaTagline={creditScoreDeltaTagline}
        isDisabled={isDisabled}
      />
      <Tracker {...trackerProps} />
      <View style={labelStyle.labelContainer}>
        {labels?.map((label: string, index: number) => {
          const TextComponent = isGradeLabel(label)
            ? BodyXSBoldText
            : BodyXSText;
          return (
            <TextComponent
              numberOfLines={1}
              key={label}
              style={[
                { flexBasis: `${trackerProps?.steps[index]?.length * 8}%` },
                labelStyle.label,
              ]}
            >
              {label}
            </TextComponent>
          );
        })}
      </View>
    </PressableWithDelay>
  );
};

const baseStyle = StyleSheet.create({
  container: {
    backgroundColor: theme.color.interactive.secondary,
    borderRadius: 4,
    padding: theme.spacing.m,
    width: '100%',
  },
  creditScoreDeltaNegative: {
    color: theme.color.status.negative,
  },
  creditScoreDeltaNoChange: {
    color: theme.color.text.primary,
  },
  creditScoreDeltaPositive: {
    color: theme.color.status.positive,
  },
  creditScoreDeltaTagline: {
    color: theme.color.text.secondary,
  },
  creditScoreText: {
    flex: 1,
  },
  creditScoreTextStack: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing.s,
  },
  deltaStackContainer: {
    alignItems: 'baseline',
    flex: 1,
  },
});

const labelStyle = StyleSheet.create({
  label: {
    flexGrow: 1,
    textAlign: 'center',
  },
  labelContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 2,
    width: '100%',
  },
});

const interactiveStyle = StyleSheet.create({
  disabledText: {
    color: theme.color.interactive.disabled.text,
  },
  focus: {
    outlineColor: theme.color.interactive.focused.border,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
  },
});
